import React, { createContext, useContext, useState, ReactNode } from 'react';

export type DialogType = 'addTrans' | 'editTrans' | 'addTransLimit' | 'editTransLimit';

export interface DialogParams {
    addTrans: {
        inAccountId?: number;
        outAccountId?: number;
        transactDate?: string;
        amount?: number;
        detail?: string;
    };
    editTrans: { transId: number };
    addTransLimit: { accountId: number, applyDate: string };
    editTransLimit: { transLimitId: number };
}

interface DialogContextType {
    openDialog: <T extends DialogType>(type: DialogType, params: DialogParams[DialogType]) => void;
    closeDialog: () => void;
    dialogType?: DialogType;
    dialogParams?: DialogParams[DialogType];
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialogType, setDialogType] = useState<DialogType | undefined>(undefined);
    const [dialogParams, setDialogParams] = useState<DialogParams[DialogType] | undefined>(undefined);

    const openDialog = <T extends DialogType>(type: T, params: DialogParams[T]) => {
        setDialogType(type);
        setDialogParams(params);
    };

    const closeDialog = () => {
        setDialogType(undefined);
        setDialogParams(undefined);
    };

    return (
        <DialogContext.Provider value={{ openDialog, closeDialog, dialogType, dialogParams }}>
            {children}
        </DialogContext.Provider>
    );
};

export const useDialog = (): DialogContextType => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within DialogProvider');
    }
    return context;
};